import React, { useMemo } from 'react';

// helpers
import useTranslation from 'hooks/useTranslation';
import { colorsTheme } from 'resources/theme/styled/colors';
import { generateUniqId } from 'helpers/commonHelpers';
import { SubmitButtonsFormValues } from '../SubmitButtons';
import { useEntityIntakeFormContext } from '../../EntityIntakeFormContext';
import { associatedIndividualsStepValidationSchema } from 'validations/intake-form/intake-form-steps';

// components
import InnerForm from './InnerForm';
import DivAlignCenter from 'components/Additional/DivAlignCenter';
import SectionTitleNavigateButton from '../SectionTitleNavigateButton';
import { AutocompleteOption, Form, SectionIntro } from '@ui';

export type IndividualFormItem = {
  id: string;
  fullName: string;
  country: string | null;
  countryInitialOption?: AutocompleteOption;
  relationship: string;
};

export interface FormValuesModel extends SubmitButtonsFormValues {
  areIndividuals: boolean | null;
  individuals: IndividualFormItem[];
}

interface IProps {
  disabled?: boolean;
  renderNavigateButtonInTitle?: boolean;
}

const AssociatedIndividuals = ({
  disabled,
  renderNavigateButtonInTitle,
}: IProps) => {
  const { t } = useTranslation(['onboarding', 'common']);
  const {
    intakeForm,
    navigateToNextStep,
    navigateToPreviousStep,
    updateIntakeFormFields,
  } = useEntityIntakeFormContext();

  const initialValues = useMemo<FormValuesModel | null>(() => {
    if (!intakeForm) {
      return null;
    }

    const intakeFormFields = intakeForm.fields;

    return {
      submitType: null,
      areIndividuals:
        typeof intakeFormFields.hasOtherBeneficialOwners === 'boolean'
          ? intakeFormFields.hasOtherBeneficialOwners
          : null,

      individuals: intakeFormFields.beneficialOwners?.length
        ? intakeFormFields.beneficialOwners.map((e) => {
            let countryInitialOption: AutocompleteOption | undefined =
              undefined;
            if (
              e.countryOfResidence &&
              intakeForm.metadata.countries[e.countryOfResidence]
            ) {
              countryInitialOption = {
                id: e.countryOfResidence,
                label: intakeForm.metadata.countries[e.countryOfResidence],
              };
            }
            return {
              id: e.id || `custom-${generateUniqId()}`,
              fullName: e.fullName || '',
              country: e.countryOfResidence || null,
              countryInitialOption,
              relationship: e.relationshipToEntity || '',
            };
          })
        : [
            {
              id: `custom-${generateUniqId()}`,
              fullName: '',
              country: null,
              relationship: '',
            },
          ],
    };
  }, [intakeForm]);

  const handleSubmit = async (values: FormValuesModel) => {
    if (!intakeForm) {
      return;
    }
    const filledIndividuals = values.individuals.filter(
      (e) => e.fullName || e.country || e.relationship,
    );
    await updateIntakeFormFields(
      {
        hasOtherBeneficialOwners:
          typeof values.areIndividuals === 'boolean'
            ? values.areIndividuals
            : null,
        beneficialOwners:
          values.areIndividuals && filledIndividuals.length
            ? filledIndividuals.map((e) => ({
                id: e.id.includes('custom-') ? null : e.id,
                fullName: e.fullName || null,
                countryOfResidence: e.country || null,
                relationshipToEntity: e.relationship || null,
              }))
            : null,
      },
      undefined,
    );
    switch (values.submitType) {
      case 'save-continue':
        navigateToNextStep();
        break;
      case 'back':
        navigateToPreviousStep();
        break;
    }
  };

  return (
    <>
      <SectionIntro
        hideDivider
        titleColor={colorsTheme.colorWhite}
        titleVariant={disabled ? 'h5' : undefined}
        description={t(
          'intake_form.associated_individuals.required_relationships_helper',
        )}
        title={
          <DivAlignCenter>
            {t('intake_form.associated_individuals.title')}
            {renderNavigateButtonInTitle ? (
              <SectionTitleNavigateButton navigateToStepKey="associated_individuals" />
            ) : null}
          </DivAlignCenter>
        }
      />
      <Form
        disabled={disabled}
        onSubmit={handleSubmit}
        renderForm={<InnerForm />}
        initialValues={initialValues}
        validationSchema={associatedIndividualsStepValidationSchema}
      />
    </>
  );
};

export default AssociatedIndividuals;
